import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

/* ───────────────────────────────────────────────
   Inventory Posting List  –  SAP B1 style
   ─────────────────────────────────────────────── */

const CHANGE_TYPES = [
  'Production Issue', 'Production Receipt', 'Transfer In', 'Transfer Out',
  'PO Receipt', 'SO Ship', 'Rework In', 'Rework Out', 'Manual Adjustment',
];

function defaultDateFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
}

/* Small reusable header‑style checkbox / radio */
const Chk = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-1 text-xs text-sap-text cursor-pointer select-none">
    <input type="checkbox" checked={checked} onChange={onChange}
      className="accent-sap-blue w-3 h-3" />
    {label}
  </label>
);
const Radio = ({ label, value, current, onChange }) => (
  <label className="flex items-center gap-1 text-xs text-sap-text cursor-pointer select-none">
    <input type="radio" name="subtotal" value={value} checked={current === value}
      onChange={() => onChange(value)} className="accent-sap-blue w-3 h-3" />
    {label}
  </label>
);

export default function InventoryPostingList() {
  const navigate = useNavigate();

  /* ── filters ── */
  const [itemId, setItemId]           = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [changeType, setChangeType]   = useState('');
  const [dateFrom, setDateFrom]       = useState(defaultDateFrom);
  const [dateTo, setDateTo]           = useState('');
  const [search, setSearch]           = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [subtotal, setSubtotal]       = useState('');           // '' | daily | monthly | yearly
  const [showSubtotal, setShowSubtotal] = useState(false);
  const [keepVisible, setKeepVisible]   = useState(false);      // mirrors SAP "Keep Visible"

  /* ── data ── */
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [loading, setLoading] = useState(true);

  /* ── filter options ── */
  const [items, setItems]           = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  /* Load filter options once */
  useEffect(() => {
    (async () => {
      try {
        const f = await api.get('/inventory-postings/filters');
        setItems(f.items || []);
        setWarehouses(f.warehouses || []);
        setTypeOptions(f.changeTypes || []);
      } catch (e) { console.error('Filters load error:', e); }
    })();
  }, []);

  /* Load data */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (itemId)      p.set('item_id', itemId);
      if (warehouseId) p.set('warehouse_id', warehouseId);
      if (changeType)  p.set('change_type', changeType);
      if (dateFrom)    p.set('from', dateFrom);
      if (dateTo)      p.set('to', dateTo);
      if (searchDebounced) p.set('search', searchDebounced);
      if (showSubtotal && subtotal) p.set('subtotal', subtotal);
      p.set('page', page);
      p.set('limit', 50);
      const data = await api.get(`/inventory-postings?${p.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { console.error('Postings load error:', e); setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [itemId, warehouseId, changeType, dateFrom, dateTo, searchDebounced, showSubtotal, subtotal, page]);

  useEffect(() => { load(); }, [load]);

  /* ── helpers ── */
  const fmt = (v) => v != null && v !== '' ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  const fmtQty = (v) => v != null && v !== '' && Number(v) !== 0 ? Number(v).toLocaleString('en-IN') : '';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';

  const docIcon = (type) => {
    if (!type) return '';
    const map = { SO: '📦', SI: '🧾', PO: '📋', IN: '📥', JM: '📒', GR: '✅', GI: '📤', TR: '🔄', BM: '🏭' };
    return map[type] || '📄';
  };

  /* Navigate to source document when yellow arrow is clicked */
  const getDocLink = (row) => {
    const id = row.related_id;
    if (!id) return null;
    const ct = row.change_type || '';
    const dt = (row.doc_type || '').toUpperCase();
    if (ct.includes('SO') || ct === 'SO Ship' || dt === 'SO' || dt === 'SI')
      return `/sales-order/${id}`;
    if (ct.includes('PO') || ct === 'PO Receipt' || dt === 'PO' || dt === 'GR')
      return `/purchase-order/${id}`;
    if (ct.includes('Production') || dt === 'JM')
      return `/production-order/${id}`;
    if (ct.includes('Rework'))
      return `/rework/${id}`;
    return null;
  };

  /* Running balance (computed client-side from reversed order since rows come DESC) */
  const displayRows = [...rows].reverse();
  let runBal = displayRows.length > 0 ? parseFloat(displayRows[0].stock_before || 0) : 0;
  const computed = displayRows.map(r => {
    runBal += parseFloat(r.quantity_change || 0);
    return { ...r, running_balance: runBal };
  });
  const finalRows = computed.reverse(); // back to DESC

  const COLS = [
    { key: 'posting_date',  label: 'Posting Date',     w: 'w-24' },
    { key: 'doc_number',    label: 'Document#',         w: 'w-28' },
    { key: 'doc_row',       label: 'Doc. Row',          w: 'w-16', right: true },
    { key: 'wh_code',       label: 'Whse',              w: 'w-14' },
    { key: 'gl_acct_code',  label: 'G/L Acct/BP Code',  w: 'w-32' },
    { key: 'gl_acct_name',  label: 'G/L Acct/BP Name',  w: 'flex-1 min-w-[160px]' },
    { key: 'rec_qty',       label: 'Rec. Qty',          w: 'w-20', right: true },
    { key: 'iss_qty',       label: 'Iss. Qty',          w: 'w-20', right: true },
    { key: 'inventory_uom', label: 'Inventory UoM',     w: 'w-22' },
    { key: 'price_after_disc', label: 'Price after Disc.', w: 'w-28', right: true },
    { key: 'balance',       label: 'Balance',           w: 'w-20', right: true },
  ];

  const filterSelectCls = 'border border-sap-border rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sap-blue/20 focus:border-sap-blue';
  const filterDateCls = 'border border-sap-border rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sap-blue/20 focus:border-sap-blue';

  return (
    <div className="space-y-4 font-sap">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Inventory Posting List</h1>
          <p className="text-sm text-sap-textLight mt-0.5">{total} posting{total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="card px-4 py-3 space-y-3">
        {/* Row 1: subtotal toggles + keep visible */}
        <div className="flex items-center gap-5 flex-wrap">
          <Chk label="Display Subtotal:" checked={showSubtotal}
            onChange={() => setShowSubtotal(v => !v)} />
          <Radio label="Daily"   value="daily"   current={subtotal} onChange={setSubtotal} />
          <Radio label="Monthly" value="monthly" current={subtotal} onChange={setSubtotal} />
          <Radio label="Yearly"  value="yearly"  current={subtotal} onChange={setSubtotal} />
          <div className="flex-1" />
          <Chk label="Keep Visible" checked={keepVisible}
            onChange={() => setKeepVisible(v => !v)} />
        </div>

        {/* Row 2: search + filters */}
        <div className="flex items-end gap-3 flex-wrap">
          {/* Find */}
          <div>
            <label className="block text-2xs text-sap-textLight mb-0.5">Find</label>
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search…"
              className={`w-44 ${filterSelectCls}`} />
          </div>
          {/* Item */}
          <div>
            <label className="block text-2xs text-sap-textLight mb-0.5">Item</label>
            <select value={itemId} onChange={e => { setItemId(e.target.value); setPage(1); }}
              className={`w-48 ${filterSelectCls}`}>
              <option value="">All Items</option>
              {items.map(i => (
                <option key={i.item_id} value={i.item_id}>{i.item_no} – {i.description}</option>
              ))}
            </select>
          </div>
          {/* Warehouse */}
          <div>
            <label className="block text-2xs text-sap-textLight mb-0.5">Warehouse</label>
            <select value={warehouseId} onChange={e => { setWarehouseId(e.target.value); setPage(1); }}
              className={`w-44 ${filterSelectCls}`}>
              <option value="">All</option>
              {warehouses.map(w => (
                <option key={w.warehouse_id} value={w.warehouse_id}>{w.wh_code} – {w.wh_name}</option>
              ))}
            </select>
          </div>
          {/* Type */}
          <div>
            <label className="block text-2xs text-sap-textLight mb-0.5">Type</label>
            <select value={changeType} onChange={e => { setChangeType(e.target.value); setPage(1); }}
              className={`w-36 ${filterSelectCls}`}>
              <option value="">All Types</option>
              {(typeOptions.length ? typeOptions : CHANGE_TYPES).map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="data-table-wrap">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="table-head">
                <th className="w-8">#</th>
                {COLS.map(c => (
                  <th key={c.key} className={`${c.right ? 'text-right' : 'text-left'} ${c.w}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-10 text-sap-textLight">Loading…</td></tr>
              ) : finalRows.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-10 text-sap-textLight">No postings found.</td></tr>
              ) : finalRows.map((r, i) => (
                <tr key={r.log_id} className={`table-row even:bg-gray-50/50 ${i % 2 === 1 ? 'bg-gray-50/50' : ''}`}>
                  <td className="px-4 py-2.5 text-sap-textLight text-xs">{(page - 1) * 50 + i + 1}</td>
                  <td className="px-4 py-2.5 text-sap-text text-xs">{fmtDate(r.posting_date || r.created_at)}</td>
                  <td className="px-4 py-2.5 text-sap-blue font-medium text-xs">
                    <span className="inline-flex items-center gap-1">
                      {getDocLink(r) && (
                        <button onClick={() => navigate(getDocLink(r))}
                          title="Open source document"
                          className="inline-flex items-center justify-center w-4 h-4 rounded
                                     bg-amber-400 hover:bg-amber-500 text-white text-[8px]
                                     shadow-sm transition-colors flex-shrink-0"
                        >&#9654;</button>
                      )}
                      {r.doc_type ? `${docIcon(r.doc_type)} ` : ''}
                      {r.doc_number || (r.related_entity && `${r.related_entity} ${r.related_id}`) || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-sap-textLight text-xs">{r.doc_row ?? ''}</td>
                  <td className="px-4 py-2.5 text-sap-text font-mono text-xs">{r.wh_code || ''}</td>
                  <td className="px-4 py-2.5 text-sap-text text-xs">{r.gl_acct_code || ''}</td>
                  <td className="px-4 py-2.5 text-sap-text text-xs">{r.gl_acct_name || r.change_type || ''}</td>
                  <td className="px-4 py-2.5 text-right text-green-700 font-medium text-xs">{fmtQty(r.rec_qty)}</td>
                  <td className="px-4 py-2.5 text-right text-red-600 font-medium text-xs">{fmtQty(r.iss_qty)}</td>
                  <td className="px-4 py-2.5 text-sap-textLight text-xs">{r.inventory_uom || r.item_uom || 'NOS'}</td>
                  <td className="px-4 py-2.5 text-right text-sap-text font-mono text-xs">
                    {r.price_after_disc ? `INR ${fmt(r.price_after_disc)}` : ''}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-sap-text text-xs">
                    {fmtQty(r.running_balance ?? r.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-sap-textLight font-medium">Posting Date From</span>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            className={filterDateCls} />
          <span className="text-sm text-sap-textLight">To</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
            className={filterDateCls} />
          {(dateFrom || dateTo) && (
            <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
              className="text-xs text-sap-blue hover:underline">Clear</button>
          )}
        </div>

        {pages > 1 && (
          <div className="flex items-center gap-1 text-xs text-sap-textLight">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors">
              ‹
            </button>
            <span className="px-2.5 py-1">{page} / {pages}</span>
            <button type="button" disabled={page >= pages} onClick={() => setPage(p => p + 1)}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors">
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
